import { Injectable, signal } from '@angular/core';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'dp-theme';

/**
 * Owns the app-wide datapad theme. Sets `data-theme` on <html>, which drives
 * both the `--dp-*` token layer and the `--mat-sys-*` Material mapping in
 * styles.scss. The choice is persisted to localStorage; on first visit it
 * defaults from the OS `prefers-color-scheme`, falling back to dark (the
 * datapad's native world).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _theme = signal<Theme>('dark');

  /** Current theme as a readonly signal. */
  readonly theme = this._theme.asReadonly();

  constructor() {
    this.apply(this.resolveInitial());
  }

  setTheme(theme: Theme): void {
    this.apply(theme);
  }

  toggle(): void {
    this.apply(this._theme() === 'dark' ? 'light' : 'dark');
  }

  private resolveInitial(): Theme {
    const stored = this.readStored();
    if (stored) {
      return stored;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  }

  private readStored(): Theme | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value === 'dark' || value === 'light' ? value : null;
    } catch {
      // localStorage can throw in private-mode / sandboxed contexts.
      return null;
    }
  }

  private apply(theme: Theme): void {
    this._theme.set(theme);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistence is best-effort; ignore storage failures.
    }
  }
}
